import { useEffect, useMemo } from 'react'
import * as THREE from 'three'
import type { DanoVeicular } from '../../dominio/didatico/danoVeicular'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import {
  deslocamentoNoPonto,
  estadoDeformacaoNoInstante,
  type CampoDeformacao,
  type EstadoDeformacaoVeiculo,
} from '../../dominio/didatico/campoDeformacao'
import { criarDescritorDeformacao } from '../../dominio/didatico/deformacaoVeicular'
import { vetor } from '../../dominio/geometria/vetor3'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { Vetor3D } from '../componentes/Vetor3D'

interface Props {
  perfil: PerfilVeiculo
  /** estado de deformação do veículo; é o que o mapa desenha */
  estado?: EstadoDeformacaoVeiculo | null
  /** forma antiga: um dano e o tempo, convertidos no mesmo estado */
  dano?: DanoVeicular
  tempo?: number
  inicio?: number
  duracao?: number
  /** identifica o veículo no rótulo (A, B) */
  rotulo?: string
}

const COR_NIVEL = {
  leve: '#f2c94c',
  moderado: '#f2994a',
  severo: '#eb5757',
} as const

const COR_BAIXA = new THREE.Color('#fde68a')
const COR_ALTA = new THREE.Color('#dc2626')

/**
 * Mapa de deformação — sobreposição DIDÁTICA, desligada por padrão.
 *
 * Antes esta casca gaussiana era a própria "lataria amassada", e é dela que
 * vinha o aspecto de bola empurrando a chapa. A deformação física agora está
 * nas peças (campo de deformação aplicado em capô, para-choque, portas...).
 * Aqui sobra o que ajuda o professor a explicar: uma pele colorida sobre a
 * face atingida, amostrando o MESMO campo (amarelo = pouco, vermelho = a
 * intrusão máxima), a direção da carga e um rótulo com zona, largura e
 * intrusão aproximada.
 */
export function DeformacaoImpacto3D({ perfil, estado, dano, tempo = 1, inicio = 0.22, duracao = 0.28, rotulo }: Props) {
  const estadoEfetivo = useMemo(() => {
    if (estado) return estado
    if (!dano) return null
    const descritor = criarDescritorDeformacao(perfil, dano, { instanteInicio: inicio, duracao, veiculoId: 'mapa' })
    return estadoDeformacaoNoInstante('mapa', [descritor], tempo)
  }, [estado, dano, perfil, inicio, duracao, tempo])

  if (!estadoEfetivo || estadoEfetivo.campos.length === 0) return null
  return (
    <group>
      {estadoEfetivo.campos.map((campo, i) => (
        <MapaDoCampo key={`${campo.regiao}:${i}`} perfil={perfil} campo={campo} estado={estadoEfetivo} rotulo={rotulo} />
      ))}
    </group>
  )
}

interface FaceMapa {
  /** ponto da grade (u, v ∈ 0..1) no referencial do veículo */
  ponto: (u: number, v: number) => [number, number, number]
  normal: [number, number, number]
  centro: [number, number, number]
}

function faceDoCampo(perfil: PerfilVeiculo, campo: CampoDeformacao): FaceMapa {
  const hw = perfil.largura / 2
  const folga = 0.035
  switch (campo.regiao) {
    case 'frente':
    case 'traseira': {
      const frente = campo.regiao === 'frente'
      const z = frente ? perfil.frenteZ + folga : perfil.traseiraZ - folga
      const yTopo = frente ? perfil.capoY + 0.04 : perfil.cinturaY + 0.04
      return {
        ponto: (u, v) => [-hw + u * 2 * hw, 0.2 + v * (yTopo - 0.2), z],
        normal: [0, 0, frente ? 1 : -1],
        centro: [campo.centroFaceM, (0.2 + yTopo) / 2, z],
      }
    }
    case 'lateralEsquerda':
    case 'lateralDireita': {
      const x = campo.sinalLateral * (hw + folga)
      const z0 = perfil.traseiraZ + 0.25
      const z1 = perfil.frenteZ - 0.25
      return {
        ponto: (u, v) => [x, 0.18 + v * (perfil.tetoY - 0.22), z0 + u * (z1 - z0)],
        normal: [campo.sinalLateral, 0, 0],
        centro: [x, campo.alturaContatoY, campo.centroFaceM],
      }
    }
    case 'teto': {
      const y = perfil.tetoY + folga
      return {
        ponto: (u, v) => [-hw * 0.9 + u * 1.8 * hw, y, perfil.teto.zTraseiro + v * (perfil.teto.zDianteiro - perfil.teto.zTraseiro)],
        normal: [0, 1, 0],
        centro: [campo.sinalLateral * hw * 0.45, y, (perfil.teto.zDianteiro + perfil.teto.zTraseiro) / 2],
      }
    }
  }
}

function MapaDoCampo({
  perfil,
  campo,
  estado,
  rotulo,
}: {
  perfil: PerfilVeiculo
  campo: CampoDeformacao
  estado: EstadoDeformacaoVeiculo
  rotulo?: string
}) {
  const face = faceDoCampo(perfil, campo)
  const geometria = useMemo(() => {
    const nu = 30
    const nv = 14
    const posicoes = new Float32Array((nu + 1) * (nv + 1) * 3)
    const cores = new Float32Array((nu + 1) * (nv + 1) * 4)
    const cor = new THREE.Color()
    const unico: EstadoDeformacaoVeiculo = { ...estado, campos: [campo] }
    for (let j = 0; j <= nv; j += 1) {
      for (let i = 0; i <= nu; i += 1) {
        const k = j * (nu + 1) + i
        const [x, y, z] = face.ponto(i / nu, j / nv)
        const d = deslocamentoNoPonto(unico, perfil, vetor(x, y, z), 'rigida')
        posicoes.set([x + d.x, y + d.y, z + d.z], k * 3)
        const t = Math.min(1, Math.hypot(d.x, d.y, d.z) / Math.max(campo.intrusaoMaxM, 1e-6))
        cor.copy(COR_BAIXA).lerp(COR_ALTA, t)
        cores.set([cor.r, cor.g, cor.b, t < 0.04 ? 0 : 0.25 + 0.55 * t], k * 4)
      }
    }
    const indices: number[] = []
    for (let j = 0; j < nv; j += 1) {
      for (let i = 0; i < nu; i += 1) {
        const a = j * (nu + 1) + i
        indices.push(a, a + 1, a + nu + 1, a + 1, a + nu + 2, a + nu + 1)
      }
    }
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.BufferAttribute(posicoes, 3))
    g.setAttribute('color', new THREE.BufferAttribute(cores, 4))
    g.setIndex(indices)
    return g
    // a chave do estado já muda a cada instante do esmagamento
  }, [perfil, campo.regiao, campo.intrusaoM, campo.centroFaceM, estado.chave])
  useEffect(() => () => geometria.dispose(), [geometria])

  const [nx, ny, nz] = face.normal
  const [cx, cy, cz] = face.centro
  const cor = COR_NIVEL[campo.nivel]
  const nomeZona =
    campo.regiao === 'frente'
      ? 'frente'
      : campo.regiao === 'traseira'
        ? 'traseira'
        : campo.regiao === 'teto'
          ? 'teto'
          : 'lateral'

  return (
    <group>
      <mesh geometry={geometria} renderOrder={7}>
        <meshBasicMaterial vertexColors transparent side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
      {/* direção da carga: de fora para dentro da face atingida */}
      <Vetor3D
        origem={vetor(cx + nx * 0.75, cy + ny * 0.75, cz + nz * 0.75)}
        destino={vetor(cx + nx * 0.08, cy + ny * 0.08, cz + nz * 0.08)}
        cor={cor}
        espessura={5}
      />
      <Rotulo3D
        posicao={[cx + nx * 0.4, perfil.tetoY + 0.2 + (rotulo ? 0.12 : 0), cz + nz * 0.4]}
        texto={
          `${rotulo ? `${rotulo} • ` : ''}${nomeZona} • ${campo.nivel} • ` +
          `largura ≈ ${campo.larguraM.toFixed(1)} m • intrusão ≈ ${Math.round(campo.intrusaoM * 100)} cm`
        }
        variante="risco"
        fator={5}
        // o mapa é ligado de propósito: a leitura dele não depende dos rótulos gerais
        sempreVisivel
      />
    </group>
  )
}
